import type { ReactNode } from "react";
import type { Messages } from "../fr";

export const auth: Messages["auth"] = {
  pageTitles: {
    login: "Log in — Hune",
    signup: "Sign up — Hune",
    forgotPassword: "Forgot password — Hune",
    resetPassword: "New password — Hune",
    onboarding: "Set up your workspace — Hune",
    emailPreferences: "Weekly summary — Hune",
    stop: "Stop receiving messages — Hune",
  },
  shell: {
    homeLabel: "Hune — home",
    legal: {
      terms: "terms of use",
      termsOfSale: "terms of sale",
      privacy: "privacy policy",
      sentence: (terms: ReactNode, termsOfSale: ReactNode, privacy: ReactNode): ReactNode[] => [
        "By continuing, you agree to Hune’s ",
        terms,
        " and ",
        termsOfSale,
        ", and acknowledge its ",
        privacy,
        ".",
      ],
    },
    testimonial: {
      quote:
        "“Before Hune, our sales reps spent hours looking for the right moment to reach out to an account. Now they start with the few signals that truly deserve a conversation.”",
      role: "SDR at AgentifAI",
    },
    sourcesPromise: "Every opportunity stays linked to its sources.",
    pitch: "Your sales monitoring, documented and prioritized by an autonomous agent.",
    privacyPromise: "Your data and your analyses stay private to your workspace.",
  },
  form: {
    modes: {
      login: {
        title: "Log in",
        submit: "Log in",
      },
      signup: {
        title: "Create your account",
        submit: "Create my account",
      },
      forgot: {
        eyebrow: "Account recovery",
        title: "Forgot your password?",
        description: "We’ll send you a secure link to choose a new password.",
        submit: "Send the link",
      },
      reset: {
        eyebrow: "New password",
        title: "Secure your account",
        description: "Choose a password of at least eight characters.",
        submit: "Update",
      },
    },
    firstAnalysisOn: "First analysis scheduled for",
    google: "Continue with Google",
    orWithEmail: "or with your email",
    email: "Email address",
    emailPlaceholder: "you@company.com",
    password: "Password",
    newPassword: "New password",
    confirmPassword: "Confirm password",
    forgotLink: "Forgot your password?",
    busy: "One moment…",
    passwordTooShort: (min: number) => `Your password must be at least ${min} characters long.`,
    passwordsDiffer: "The two passwords don’t match.",
    resetSent: "If this address exists, a reset link has just been sent.",
    passwordUpdated: "Password updated. Taking you to your workspace…",
    failed: "Something went wrong.",
    noAccount: "Don’t have an account yet?",
    signupLink: "Sign up",
    hasAccount: "Already have an account?",
    loginLink: "Log in",
    backToLogin: "Back to log in",
  },
  resetPassword: {
    invalidLink: "This link is invalid or has expired. Request a new one.",
    subject: "this page",
  },
  onboarding: {
    steps: {
      label: "Steps",
      you: "You",
      site: "Your website",
      last: "Last question",
    },
    roles: {
      founder: "Founder or executive",
      sales: "Sales",
      marketing: "Marketing",
      agency: "Agency or consultant",
      other: "Other",
    },
    sources: {
      google: "Google search",
      linkedin: "LinkedIn",
      referral: "A friend or colleague",
      email: "An email from Hune",
      content: "An article or podcast",
      event: "An event",
      ad: "An ad",
      other: "Other",
    },
    switchAccount: "Switch account",
    stepCounter: (step: number, total: number) => `${step} / ${total}`,
    titles: {
      welcome: "Welcome",
      welcomeName: (firstName: string) => `Welcome, ${firstName}`,
      welcomeLedePrefilled:
        "Check these details: they’re used for your workspace and for our exchanges. Only two steps left after this.",
      welcomeLede: "A few details for your workspace and our exchanges. Only two steps left after this.",
      site: "What do you sell?",
      siteLede:
        "The address of the page that presents your product or service. That’s all the agent needs to understand your market.",
      source: "How did you hear about Hune?",
      sourceLede: "Optional. Your answer tells us where to keep talking about Hune.",
    },
    fields: {
      firstName: "First name",
      lastName: "Last name",
      prefilled: "Taken from your account",
      company: "Company",
      role: "Your role",
      optional: "— optional",
      otherRoleLabel: "Specify your role",
      otherRolePlaceholder: "Specify: partnerships manager, office manager…",
      website: "Your website address",
      websitePlaceholder: "your-website.com",
      websiteHint: "A specific product page gives a sharper target than the home page.",
      websiteUnreachable: "The site isn’t responding: check the address. You can continue if it’s correct.",
      sourceLabel: "How did you hear about Hune?",
    },
    siteStatus: {
      checking: "Checking",
      ok: "Page found",
      unreachable: "Not found",
    },
    errors: {
      invalidSite: "Enter the address of a public website, for example your-website.com.",
      saveFailed: "Couldn’t save your profile.",
    },
    actions: {
      back: "Back",
      continue: "Continue",
      launch: "Start my first analysis",
      launching: "Starting your analysis…",
      or: "or",
      enterKey: "Enter ↵",
      launchAnyway: "Without an answer, the button starts the analysis anyway.",
    },
    agentPlan: {
      title: "What the agent will do",
      timing: "About ten minutes after the last step.",
      readsSite: (site: string) => `It reads ${site}`,
      readsYourSite: "It reads your website",
      readsSiteBody: "What you sell, to whom, and what sets you apart.",
      target: "It works out your target",
      targetBody: "The priority segment, the decision-maker, the moments when the need becomes urgent.",
      signals: "It looks for signals",
      signalsBody: "Press, job postings, public tenders, registers, LinkedIn.",
      leads: "Your first leads arrive",
      leadsBody: "Dated, sourced, with the right contacts. You can adjust the target whenever you like.",
    },
    summary: {
      title: "Ready to start",
      editable: "You can change everything later.",
      you: "You",
      company: "Company",
      site: "Website analyzed",
      edit: "Edit",
      editTarget: (term: string) => `: ${term.toLowerCase()}`,
      noContact: "Hune never contacts anyone on your behalf.",
    },
  },
  account: {
    firstName: "First name",
    lastName: "Last name",
    email: "Email",
    company: "Company",
    role: "Role",
    website: "Product website",
    save: "Save",
    saved: "Saved",
    changePassword: "Change password",
  },
  emailPreferences: {
    title: "Stop receiving the weekly summary",
    done: "Done.",
    doneBody:
      "You will no longer receive the weekly summary. Monitoring continues: new leads stay in your pipeline. You can turn it back on at any time in your settings, under “Notifications”.",
    body: "The summary goes out once a week at most, and only when the agent has found something new for your offers. One click turns it off; monitoring carries on.",
    submit: "Stop receiving it",
    manage: "Manage your emails in settings",
    privacy: "Privacy policy",
  },
  stop: {
    title: "Stop receiving messages from Hune",
    done: "Done.",
    doneBody: (brand: string) =>
      `Your address will no longer receive any prospecting email from ${brand}, follow-ups included. This is permanent: only a fingerprint of your address is kept, so that we can honor it.`,
    body: (brand: string) =>
      `One click is enough: your address will be permanently removed from ${brand}’s prospecting. You can also reply “stop” to any of our messages.`,
    submit: "Stop receiving messages",
    question: "A question about your data:",
    privacy: "Privacy policy",
  },
  tourWelcome: {
    video: "Watch Hune",
    exploring: "The agent is exploring your market",
    progress: (pages: number | null) => {
      const duration = "about ten minutes in total";
      if (!pages) return duration;
      return `${pages === 1 ? "1 page read" : `${pages} pages read`} · ${duration}`;
    },
    ideas: {
      searches: { title: "It searches", body: "Dated, sourced signals in your market." },
      contact: { title: "You reach out", body: "A ready message for each contact, sent by you." },
      continues: { title: "It keeps going", body: "Monitoring runs again on its own and only shows what’s new." },
    },
    start: "Explore my workspace",
  },
};
